import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { getCurrentAdmin, logout } from "../../utils/api";
import LoadingOverlay from "../../components/LoadingOverlay";
import dicomLogo from "../../images/recOrbDicom_icon.png";
import appLogo from "../../images/recOrb_icon.png";
import recOrbActive from "../../images/recorb-tab.svg";
import recOrb from "../../images/recorb-tab-no-active.svg";

const AdminLayout: React.FC = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [role, setRole] = useState<"SUPER" | "SUB" | undefined>(undefined);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const me = await getCurrentAdmin();
        setRole(me.role);
      } catch {}
    })();
  }, []);
  return (
    <div className="flex min-h-screen bg-white w-full">
      {/* Sidebar */}
      <aside className="w-[174px] border-r border-gray-200 p-4 px-[12px] box-border bg-white flex flex-col justify-between h-screen fixed">
        <div>
          <div className="flex justify-center pb-4">
            <img src="/logo/logo.svg" alt="Takagi" className="w-36" />
          </div>
          <nav className="flex flex-col gap-2 mt-4">
            <NavLink
              to="/admin/apps"
              className={({ isActive }) =>
                [
                  "rounded-md px-3 py-2 no-underline text-left h-[45px] flex items-center gap-[10px] text-sm",
                  isActive || location.pathname === '/admin'
                    ? "bg-[#0077C8] text-white active-link"
                    : "text-gray-800 hover:bg-gray-200",
                ].join(" ")
              }
            >
              <img src={appLogo} alt="appLogo" className="h-[22px]" />
              アプリ管理
            </NavLink>
            <NavLink
              to="/admin/recorb-management"
              className={({ isActive }) =>
                [
                  "rounded-md px-3 py-2 no-underline text-left h-[45px] flex items-center gap-[10px] text-sm",
                  isActive
                    ? "bg-[#0077C8] text-white active-link"
                    : "text-gray-800 hover:bg-gray-200",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <img
                    src={isActive ? recOrbActive : recOrb}
                    alt="recOrb"
                    className="h-[22px]"
                  />
                  <p className="mb-0 min-w-[96px]">RecOrb</p>
                </>
              )}
            </NavLink>
            <NavLink
              to="/admin/license-management"
              className={({ isActive }) =>
                [
                  "rounded-md px-3 py-2 no-underline text-left h-[45px] flex items-center gap-[10px] text-sm",
                  isActive
                    ? "bg-[#0077C8] text-white active-link"
                    : "text-gray-800 hover:bg-gray-200",
                ].join(" ")
              }
            >
              <img src={dicomLogo} alt="dicomLogo" className="h-[22px]" />
              <p className="mb-0 min-w-[96px]">RecOrb DICOM</p>
            </NavLink>
            {role === "SUPER" && (
              <NavLink
                to="/admin/accounts"
                className={({ isActive }) =>
                  [
                    "rounded-md px-3 py-2 no-underline text-left",
                    isActive
                      ? "bg-[#0077C8] text-white active-link"
                      : "text-gray-800 hover:bg-gray-200",
                  ].join(" ")
                }
              >
                アカウント管理
              </NavLink>
            )}
          </nav>
        </div>

        {/* Bottom actions */}
        <div className="pt-4">
          <NavLink
            to="/admin/account-edit"
            title="ログイン情報変更"
            aria-label="ログイン情報変更"
            className={({ isActive }) =>
              [
                "mb-2 w-full flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 no-underline text-sm",
                isActive
                  ? "bg-[#0077C8] text-[#0077C8] border-[#0077C8]"
                  : "text-gray-700 hover:bg-gray-100",
              ].join(" ")
            }
          >
            {/* Edit Account icon (inline SVG) */}
            ログイン情報変更
          </NavLink>
          <button
            onClick={() => setConfirmOpen(true)}
            title="ログアウト"
            aria-label="ログアウト"
            className="w-full flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-700 hover:bg-gray-100"
          >
            {/* Logout icon (inline SVG) */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-6 h-6"
            >
              <path d="M16 17l5-5-5-5v3H9v4h7v3z" />
              <path d="M14 3H6c-1.1 0-2 .9-2 2v14a2 2 0 002 2h8a2 2 0 002-2v-2h-2v2H6V5h8v2h2V5a2 2 0 00-2-2z" />
            </svg>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <section className="flex-1 px-6 py-4 box-border overflow-auto ml-[174px]">
        <Outlet />
      </section>

      {/* Confirm Logout Modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setConfirmOpen(false)}
            aria-hidden="true"
          />
          {/* Dialog */}
          <div
            className="relative z-10 w-[90%] max-w-md rounded-lg bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
          >
            <h2 className="text-xl font-semibold mb-3">ログアウトを確認</h2>
            <p className="text-gray-700 mb-6">
              ログアウトしてもよろしいでしょうか。
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => {
                  logout();
                  setConfirmOpen(false);
                }}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Global loading overlay for admin area */}
      <LoadingOverlay />
    </div>
  );
};

export default AdminLayout;
